/**
 * Send feedback from inside the app, the browser's part: whether the dialog is open, the draft (memory only, so closing
 * the dialog by mistake keeps it; gone on reload), what goes with the post, and the sign-in to post as. The request,
 * the proof-of-work and the answers are lib/feedbackForm.ts; the dialog is ui/Feedback.tsx.
 *
 * A browser that never signed in never touches Supabase here: the access token is asked for only when a session is
 * already signed in (lib/auth.ts has it loaded by then).
 */
import { create } from "zustand";
import { accessToken, useAccount } from "./auth.ts";
import { APP_VERSION, desktopVersion, uaHints } from "./crash.ts";
import { describeUserAgent } from "./crashReport.ts";
import { isDesktop } from "./desktop.ts";
import { feedbackContext, pageLabel, type FeedbackContext } from "./feedbackForm.ts";

interface FeedbackState {
  open: boolean;
  title: string;
  body: string;
  set: (p: Partial<FeedbackState>) => void;
}

export const useFeedback = create<FeedbackState>((set) => ({ open: false, title: "", body: "", set: (p) => set(p) }));

export function openFeedback() {
  useFeedback.getState().set({ open: true });
}
export function closeFeedback() {
  useFeedback.getState().set({ open: false });
}
/** after a post went: the next one starts empty */
export function clearFeedbackDraft() {
  useFeedback.getState().set({ title: "", body: "" });
}

/**
 * What goes with the post, worked out once when the dialog opens (so the line it shows is what's sent): MC Planner's
 * version, the system, and the screen (`pageLabel`: the demo island or a world, an open panel; never a name).
 */
export async function feedbackFacts(page: { demo?: boolean; panel?: string | null; board?: boolean }): Promise<FeedbackContext> {
  const desktop = isDesktop();
  const [hints, desktopV] = await Promise.all([
    uaHints().catch(() => undefined),
    desktop ? desktopVersion().catch(() => null) : Promise.resolve(null),
  ]);
  const ua = describeUserAgent(navigator.userAgent ?? "", hints, desktop);
  return feedbackContext({ version: (desktop && desktopV) || APP_VERSION, desktop, os: ua.os, browser: ua.browser, page: pageLabel(page) });
}

/** Signed in right now (then a post goes as that account). */
export const isSignedIn = () => useAccount.getState().status === "signed-in";

/** The signed-in person's current Lab43 access token (refreshed by supabase-js if needed); null for everyone else. */
export async function feedbackAccessToken(): Promise<string | null> {
  if (!isSignedIn()) return null;
  try {
    return await accessToken();
  } catch {
    return null;
  }
}
